import { useEffect, useRef } from "react";

/**
 * useHIDScanner — captures barcode input from a USB / Bluetooth wireless barcode scanner
 * that emulates a keyboard (like PRIMESAGE® and 99% of similar retail scanners).
 *
 * Detection heuristic:
 *  - Multiple keystrokes arriving within `interKeyMs` (~30 ms) of each other
 *  - Ending with the Enter key
 *  - Total length >= `minLength` (default 4)
 *
 * When a scanner-like burst is detected, `onScan(code)` is called and the buffer resets.
 *
 * Only listens when `enabled` is true AND the active element is NOT a text input
 * (so it doesn't interfere with normal typing).
 */
export default function useHIDScanner({ onScan, enabled = true, minLength = 4, interKeyMs = 40 }) {
  const bufRef = useRef("");
  const lastRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const t = (el.tagName || "").toLowerCase();
      const type = (el.getAttribute && el.getAttribute("type") || "").toLowerCase();
      if (t === "textarea" || t === "select" || el.isContentEditable) return true;
      if (t === "input") {
        // Allow scanner in "search" and unknown-type inputs; block in text/number/email/password etc.
        const block = ["text", "number", "email", "password", "tel", "url"];
        if (block.includes(type)) return true;
      }
      return false;
    };

    const handler = (e) => {
      // Skip if the user is typing into a normal input
      if (isTypingTarget(document.activeElement)) return;

      const now = Date.now();
      const dt = now - lastRef.current;

      if (e.key === "Enter") {
        const code = bufRef.current;
        bufRef.current = "";
        lastRef.current = 0;
        if (code.length >= minLength && dt < 500) {
          // was a fast burst → treat as scanner
          e.preventDefault();
          onScan(code);
        }
        return;
      }

      // Ignore modifier / navigation keys
      if (e.key.length !== 1) return;

      if (dt > interKeyMs && bufRef.current.length > 0) {
        // gap too large → reset buffer (probably human typing)
        bufRef.current = "";
      }
      bufRef.current += e.key;
      lastRef.current = now;
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, onScan, minLength, interKeyMs]);
}
